import { useRef, useEffect, useCallback } from 'react';

// Declare process for browser environment
declare const process: {
  env: {
    NODE_ENV: string;
  };
};

// Global flag to track hot reload state
let isCurrentlyHotReloading = false;

// Helper to detect if we're in a hot reload cycle
function isHotReloading(): boolean {
  // Check if we have React refresh context or hot reloading indicators
  if (typeof window !== 'undefined') {
    // Look for React refresh indicators in the call stack
    const stack = new Error().stack ?? '';
    const hasStackIndicators = stack.includes('@react-refresh') ||
      stack.includes('performReactRefresh') ||
      stack.includes('validateRefreshBoundaryAndEnqueueUpdate');

    // Also check for Vite HMR indicators
    const hasViteIndicators = (window as unknown as { __vite_plugin_react_preamble_installed__?: boolean })
      .__vite_plugin_react_preamble_installed__ === true;

    // Check for recent hot reload activity (within last 2 seconds)
    const hasRecentReload = isCurrentlyHotReloading;

    return hasStackIndicators || hasViteIndicators || hasRecentReload;
  }
  return false;
}

// Mark hot reload as active
export function markHotReloadActive(): void {
  isCurrentlyHotReloading = true;
  // Clear the flag after a short delay
  setTimeout(() => {
    isCurrentlyHotReloading = false;
  }, 2000);
}

interface RenderTracker {
  (): void;
  lastRenderTime?: number;
  firstRenderTime?: number;
  renderCount?: number;
  hasWarned?: boolean;
}

/**
 * Hook to detect memory leaks by tracking operations that occur after component unmount.
 * Provides utilities to safely handle async operations, timers, and subscriptions.
 *
 * @param componentName - Optional name for the component (used in warnings)
 * @returns Object with utilities for safe operation handling
 *
 * @example
 * ```tsx
 * const { isMounted, safeAsync, safeTimeout, safeInterval, safeSubscribe } = useMemoryLeakDetector('MyComponent');
 *
 * useEffect(() => {
 *   const fetchData = async () => {
 *     const result = await safeAsync(fetch('/api/data'));
 *     if (isMounted()) {
 *       setData(result);
 *     }
 *   };
 *   fetchData();
 * }, []);
 *
 * useEffect(() => {
 *   const timer = safeTimeout(() => {
 *     // This will only execute if component is still mounted
 *     console.log('Timer executed');
 *   }, 1000);
 *
 *   return () => clearTimeout(timer);
 * }, []);
 * ```
 */
export function useMemoryLeakDetector(componentName = 'UnknownComponent') {
  const isMountedRef = useRef(true);
  const operationCountRef = useRef(0);

  // Track when component unmounts
  useEffect(() => {
    return () => {
      isMountedRef.current = false;

      // Warn about potential memory leaks if operations were still active
      if (operationCountRef.current > 0 && process.env.NODE_ENV === 'development') {
        console.warn(
          // eslint-disable-next-line react-hooks/exhaustive-deps
          `[MemoryLeakDetector] ${componentName} unmounted with ${operationCountRef.current} active operation(s). ` +
            'This may indicate a memory leak. Ensure all async operations, timers, and subscriptions are properly cleaned up.'
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // componentName is stable and doesn't need to be a dependency

  /**
   * Check if component is still mounted
   */
  const isMounted = useCallback(() => isMountedRef.current, []);

  /**
   * Safely execute an async operation with leak detection
   */
  const safeAsync = useCallback(
    async <T>(promise: Promise<T>, abortSignal?: AbortSignal, allowCompletionAfterUnmount = false): Promise<T | null> => {
      operationCountRef.current++;

      // If already aborted, don't start the operation
      if (abortSignal?.aborted) {
        operationCountRef.current--;
        return null;
      }

      try {
        const result = await promise;
        operationCountRef.current--;

        // Check if component unmounted or operation was aborted during execution
        if (
          (!isMountedRef.current || abortSignal?.aborted) &&
          process.env.NODE_ENV === 'development'
        ) {
          // Skip warning during hot reloading as this is expected behavior
          // Also skip warning for operations that are allowed to complete after unmount
          if (!isHotReloading() && !allowCompletionAfterUnmount) {
            console.warn(
              `[MemoryLeakDetector] ${componentName}: Async operation completed after component unmounted or aborted. ` +
                'Result will be discarded to prevent state updates on unmounted component.'
            );
          }

          // If operation is allowed to complete after unmount, return the result
          // This is useful for auth operations that update global state
          if (allowCompletionAfterUnmount) {
            return result;
          }

          return null;
        }

        return result;
      } catch (error) {
        operationCountRef.current--;

        // Check if component unmounted or operation was aborted during execution
        if (
          (!isMountedRef.current || abortSignal?.aborted) &&
          process.env.NODE_ENV === 'development'
        ) {
          // Skip warning during hot reloading as this is expected behavior
          // Also skip warning for operations that are allowed to complete after unmount
          if (!isHotReloading() && !allowCompletionAfterUnmount) {
            console.warn(
              `[MemoryLeakDetector] ${componentName}: Async operation failed after component unmounted or aborted. ` +
                'Error will be discarded to prevent side effects on unmounted component.'
            );
          }

          // If operation is allowed to complete after unmount, still throw the error
          // This maintains error handling for auth operations
          if (allowCompletionAfterUnmount) {
            throw error;
          }

          return null;
        }

        throw error;
      }
    },
    [componentName]
  );

  /**
   * Create a safe timeout that only executes if component is mounted
   */
  const safeTimeout = useCallback(
    (callback: () => void, delay: number): ReturnType<typeof setTimeout> => {
      operationCountRef.current++;

      const timeoutId = setTimeout(() => {
        operationCountRef.current--;

        if (isMountedRef.current) {
          callback();
        } else if (process.env.NODE_ENV === 'development') {
          // Skip warning during hot reloading as this is expected behavior
          if (!isHotReloading()) {
            console.warn(
              `[MemoryLeakDetector] ${componentName}: Timeout callback attempted to execute after component unmounted. ` +
                'Callback was skipped to prevent memory leaks.'
            );
          }
        }
      }, delay);

      return timeoutId;
    },
    [componentName]
  );

  /**
   * Create a safe interval that only executes if component is mounted
   */
  const safeInterval = useCallback(
    (callback: () => void, delay: number): ReturnType<typeof setInterval> => {
      operationCountRef.current++;

      const intervalId = setInterval(() => {
        if (isMountedRef.current) {
          callback();
        } else {
          // Auto-cleanup interval if component unmounted
          clearInterval(intervalId);
          operationCountRef.current--;

          if (process.env.NODE_ENV === 'development') {
            // Skip warning during hot reloading as this is expected behavior
            if (!isHotReloading()) {
              console.warn(
                `[MemoryLeakDetector] ${componentName}: Interval was auto-cleaned up after component unmounted. ` +
                  'Consider manually clearing intervals in cleanup functions.'
              );
            }
          }
        }
      }, delay);

      return intervalId;
    },
    [componentName]
  );

  /**
   * Safely subscribe to events/observables with automatic cleanup
   */
  const safeSubscribe = useCallback(
    <T>(
      subscribeFn: (callback: (value: T) => void) => (() => void) | { unsubscribe: () => void }
    ): (() => void) => {
      operationCountRef.current++;

      let unsubscribed = false;

      const subscription = subscribeFn((_value: T) => {
        if (!unsubscribed && isMountedRef.current) {
          // Handle the subscription value
        } else if (
          !unsubscribed &&
          !isMountedRef.current &&
          process.env.NODE_ENV === 'development'
        ) {
          // Skip warning during hot reloading as this is expected behavior
          if (!isHotReloading()) {
            console.warn(
              `[MemoryLeakDetector] ${componentName}: Subscription callback received data after component unmounted. ` +
                'Data was discarded to prevent memory leaks.'
            );
          }
        }
      });

      const unsubscribe =
        typeof subscription === 'function' ? subscription : subscription.unsubscribe;

      return () => {
        if (!unsubscribed) {
          unsubscribed = true;
          operationCountRef.current--;
          unsubscribe();
        }
      };
    },
    [componentName]
  );

  /**
   * Track render frequency for components with frequent updates
   */
  const renderTracker: RenderTracker = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      const now = Date.now();
      renderTracker.lastRenderTime = now;

      if (!renderTracker.firstRenderTime) {
        renderTracker.firstRenderTime = now;
        renderTracker.renderCount = 0;
      }

      renderTracker.renderCount!++;

      // Only warn about excessive renders once per component to avoid spam
      // Warn about very high render frequency (>20 renders per second for extended period)
      if (
        renderTracker.renderCount! > 100 &&
        !renderTracker.hasWarned &&
        renderTracker.firstRenderTime
      ) {
        const timeSpan = now - renderTracker.firstRenderTime;
        const rendersPerSecond = (renderTracker.renderCount! / timeSpan) * 1000;

        if (rendersPerSecond > 20) {
          console.warn(
            `[MemoryLeakDetector] ${componentName}: High render frequency detected (${rendersPerSecond.toFixed(1)} renders/sec). ` +
              'This may indicate performance issues or unnecessary re-renders. Consider optimizing with React.memo, useMemo, or useCallback.'
          );
          renderTracker.hasWarned = true;
        }
      }
    }
  }, [componentName]);

  return {
    isMounted,
    safeAsync,
    safeTimeout,
    safeInterval,
    safeSubscribe,
    renderTracker,
    activeOperations: operationCountRef.current,
  };
}
